import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../modules/auth';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import {
    Box, Tabs, Tab, Typography, Modal, Button, TextField, Divider, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ImageIcon from '@mui/icons-material/Image';
import DnsIcon from '@mui/icons-material/Dns';
import TuneIcon from '@mui/icons-material/Tune';
import LabelIcon from '@mui/icons-material/Label';
import { useHttpClient, useAlert } from '../../../modules/services/Bundle';
import { RequestMethod } from '../../../modules/services/core/_enums';
import GerenciadorBackground from './GerenciadorBackground';
import FieldConfiguration from './FieldConfiguration';
import TableColumnConfig from './TableColumnConfig';

// --- Interfaces e Tipos ---
interface CorporationData {
    CorporationId: number;
    CorporationName: string;
}

interface OrganizationFromApi {
    Id: number;
    Name: string;
}

interface LinhasMovimentacoesAtivos {
    Id: number;
    Linhas: number;
    IdOrganizacao: number;
}

// --- Componente da Modal ---
const AddOrganizationModal: React.FC<{
    open: boolean; onClose: () => void; organizations: OrganizationFromApi[];
    productionLines: LinhasMovimentacoesAtivos[]; onSubmit: (organizationId: number, linesCount: number) => void;
    corporationName: string;
}> = ({ open, onClose, organizations, productionLines, onSubmit, corporationName }) => {
    const [selectedOrgId, setSelectedOrgId] = useState<number>(-1);
    const [linesCount, setLinesCount] = useState<number>(1);
    const handleSubmit = () => {
        if (selectedOrgId === -1) { alert('Selecione uma organização'); return; }
        onSubmit(selectedOrgId, linesCount);
        onClose();
    };
    return (
        <Modal open={open} onClose={onClose} aria-labelledby="add-organization-modal" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ backgroundColor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, width: 500, maxWidth: '90vw' }}>
                <Typography variant="h6" component="h2" gutterBottom>Adicionar Linhas de Movimentação</Typography>
                <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                    <InputLabel id="organization-select-label">Organização</InputLabel>
                    <Select
                        labelId="organization-select-label" value={selectedOrgId} label="Organização"
                        onChange={(e) => setSelectedOrgId(Number(e.target.value))}
                    >
                        <MenuItem value={-1} disabled>Selecione uma organização</MenuItem>
                        {organizations
                            .filter(org => !productionLines.some(l => l.IdOrganizacao === org.Id))
                            .map((org) => (
                                <MenuItem key={org.Id} value={org.Id}>{`${corporationName} - ${org.Name}`}</MenuItem>
                            ))}
                    </Select>
                </FormControl>
                <Divider sx={{ my: 2 }} />
                <TextField fullWidth label="Número de Linhas" type="number" inputProps={{ min: 1 }} value={linesCount}
                    onChange={(e) => setLinesCount(Math.max(1, parseInt(e.target.value) || 1))} sx={{ mt: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button variant="outlined" onClick={onClose}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={selectedOrgId === -1}>Adicionar</Button>
                </Box>
            </Box>
        </Modal>
    );
};

// --- Componente de Painel de Abas ---
function TabPanel(props: { children?: React.ReactNode; index: number; value: number; }) {
    const { children, value, index } = props;
    return <div hidden={value !== index}><Box sx={{ p: 3 }}>{children}</Box></div>;
}

// --- Componente Principal da Tela ---
const EditarCorporacao: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { auth } = useAuth();
    const httpClient = useHttpClient();
    const alert = useAlert();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const [corporationData, setCorporationData] = useState<CorporationData | null>(null);
    const [organizations, setOrganizations] = useState<OrganizationFromApi[]>([]); // Estado para dados brutos da API
    const [productionLines, setProductionLines] = useState<LinhasMovimentacoesAtivos[]>([]); // Estado para dados brutos da API
    const [tableData, setTableData] = useState<any[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshData, setRefreshData] = useState(0);

    const tableRef = useRef<HTMLDivElement>(null);
    const tabulatorRef = useRef<Tabulator | null>(null);

    const applicationId = process.env.REACT_APP_POWER_BI_APPLICATIONID || '';



    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const dataParam = params.get('data');
        if (dataParam) {
            try {
                const decodedData: CorporationData = JSON.parse(decodeURIComponent(dataParam));
                setCorporationData(decodedData);
            } catch (e) { setError("Dados da corporação inválidos na URL."); setIsLoading(false); }
        } else {
            setError("Dados da corporação não encontrados na URL.");
            setIsLoading(false);
        }
    }, [location]);

    useEffect(() => {
        const loadAndProcessData = async (corpId: number) => {
            setIsLoading(true);
            setError(null);
            try {
                const [orgsResponse, linesResponse] = await Promise.all([
                    fetch(`${backendUrl}/api/Organization/Corp/${corpId}`),
                    fetch(`${backendUrl}/api/LinhasMovimentacoesAtivos`)
                ]);

                if (!orgsResponse.ok) throw new Error('Falha ao buscar organizações.');
                if (!linesResponse.ok) throw new Error('Erro ao obter linhas de movimentação.');

                const orgsData: OrganizationFromApi[] = await orgsResponse.json();
                const linesData: LinhasMovimentacoesAtivos[] = await linesResponse.json();

                setOrganizations(orgsData);
                setProductionLines(linesData);

                if (corporationData) {
                    const mergedData = orgsData.map(org => {
                        const linha = linesData.find(l => l.IdOrganizacao === org.Id);
                        return {
                            CorporationName: corporationData.CorporationName, OrganizationName: org.Name,
                            Linhas: linha ? linha.Linhas : 0, OrganizationId: org.Id, Id: linha ? linha.Id : null,
                            IdCorporacao: corporationData.CorporationId,
                        };
                    });
                    setTableData(mergedData);
                }
            } catch (err: any) {
                setError(err.message || 'Ocorreu um erro ao carregar os dados.');
            } finally {
                setIsLoading(false);
            }
        };

        if (corporationData?.CorporationId) {
            loadAndProcessData(corporationData.CorporationId);
        }
    }, [corporationData, backendUrl, refreshData]);

    useEffect(() => {
        // Só executa se o loading acabou, o div da tabela está no DOM E a aba ativa é a de organizações
        if (!isLoading && tableRef.current && activeTab === 0) {
            // Se a instância do Tabulator ainda não existe, cria ela.
            if (!tabulatorRef.current) {
                tabulatorRef.current = new Tabulator(tableRef.current, {
                    data: tableData,
                    height: "400px",
                    layout: "fitColumns",
                    placeholder: "Nenhuma organização encontrada",
                    columns: [
                        { title: "Organização", field: "OrganizationName" },
                        { title: "Linhas de Movimentação", field: "Linhas", hozAlign: "center", width: 200 },
                        {
                            title: "Ações", formatter: () => "<button class='btn-editar'>Editar Organização</button>",
                            width: 210, hozAlign: "center", headerSort: false,
                            cellClick: (e, cell) => handleEditOrganization(cell.getRow().getData()),
                        }
                    ],
                });
                // garante que a tabela redesenhe quando os dados iniciais chegarem
                tabulatorRef.current.setData(tableData).catch(() => { });
            } else {
                // Se a instância já existe, apenas atualiza os dados.
                tabulatorRef.current.setData(tableData);
            }
        }
    }, [isLoading, tableData, activeTab]);

    useEffect(() => {
        return () => {
            if (tabulatorRef.current) {
                try { tabulatorRef.current.destroy(); } catch { /* silencioso */ }
                tabulatorRef.current = null;
            }
        };
    }, []);

    const handleEditOrganization = (rowData: any) => {
        const orgData = encodeURIComponent(JSON.stringify(rowData));
        navigate(`/administracao/editar-organizacao?data=${orgData}`);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };
    const thinTabStyle = {
        minHeight: '48px', // Reduz a altura mínima padrão (geralmente 72px com ícone)
        padding: '6px 16px', // Reduz o preenchimento vertical
    };


    const handleAddOrganization = async (organizationId: number, linesCount: number) => {
        try {
            const response = await httpClient.request({
                method: RequestMethod.POST,
                endpoint: '/api/LinhasMovimentacoesAtivos',
                data: { IdOrganizacao: organizationId, Linhas: linesCount }
            });
            if (response.success) {
                alert.createMessage({ icon: 'success', message: 'Adicionado com sucesso' });
                setRefreshData(prev => prev + 1);
            } else {
                throw new Error((response as any).message || 'Erro ao adicionar.');
            }
        } catch (error) {
            alert.createMessage({ type: 'error', message: (error instanceof Error ? error.message : 'Erro desconhecido') });
        }
    };

    if (isLoading && !corporationData) return <Typography>Carregando...</Typography>;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <div className="card mb-5 mb-xl-10">
            <div className="card-header d-flex justify-content-between align-items-center border-0 pt-5">
                <Typography variant="h3" component="h2" className="card-title align-items-start flex-column">
                    <span className="card-label fw-bolder fs-3 mb-1">Corporação: {corporationData?.CorporationName}</span>
                    <span className="text-muted mt-1 fw-bold fs-7">Gerencie as configurações da corporação</span>
                </Typography>
                <div>
                    {activeTab === 0 && (
                        <Button variant="contained" onClick={() => setModalOpen(true)}>Adicionar Linhas</Button>
                    )}
                </div>
            </div>
            <div className="card-body py-3">
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={handleTabChange}>
                            <Tab sx={thinTabStyle} icon={<SettingsIcon />} iconPosition="start" label="Organizações" />
                            <Tab sx={thinTabStyle} icon={<ImageIcon />} iconPosition="start" label="Imagens" />
                            <Tab sx={thinTabStyle} icon={<DnsIcon />} iconPosition="start" label="Instâncias" />
                            <Tab sx={thinTabStyle} icon={<TuneIcon />} iconPosition="start" label="Campos" />
                            <Tab sx={thinTabStyle} icon={<LabelIcon />} iconPosition="start" label="Tabelas(Labels)" />
                        </Tabs>
                    </Box>

                    <TabPanel value={activeTab} index={0}>
                        {isLoading ? <Typography>Carregando...</Typography> : <div ref={tableRef} style={{ width: '100%' }} />}
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        {corporationData && (
                            <GerenciadorBackground
                                corporationName={corporationData.CorporationName}
                                backendUrl={backendUrl || ''}
                            />
                        )}
                    </TabPanel>

                    <TabPanel value={activeTab} index={2}>
                        <Typography>Conteúdo da aba Instâncias</Typography>
                    </TabPanel>

                    <TabPanel value={activeTab} index={3}>
                        {corporationData ? (
                            <FieldConfiguration
                                corporationName={corporationData.CorporationName}
                                applicationId={applicationId}
                            />
                        ) : (
                            <Typography>Carregando dados da corporação...</Typography>
                        )}
                    </TabPanel>
                    <TabPanel value={activeTab} index={4}>
                        {corporationData ? (
                            <TableColumnConfig corporationName={corporationData.CorporationName} />
                        ) : (
                            <Typography>Selecione uma corporação para editar os rótulos.</Typography>
                        )}
                    </TabPanel>
                </Box>
            </div>
            {corporationData && (
                <AddOrganizationModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    organizations={organizations}
                    productionLines={productionLines}
                    onSubmit={handleAddOrganization}
                    corporationName={corporationData.CorporationName}
                />
            )}
        </div>
    );
};

export default EditarCorporacao;